import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { startOfMonth } from 'date-fns'
import { push } from 'connected-react-router'
import { Spacer } from 'components/Utility'
import { SmallHeadline } from 'components/Typography'
import { Tabs } from 'components/Tab'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { SearchForm, SelectBox } from 'components/Form'
import { useCheckbox, useSelectCallback, useStringChangeEvent } from 'hooks/form'
import { useSkip } from 'hooks/effect'
import { FilterLabel, OrderContainer, OrderStatusLabels, OrderTypeCheckboxes } from 'components/Order'
import { BasketType } from 'types/basket'
import { OrderTime, OrderType } from 'types/checkout'
import { fetchBaskets, initBaskets, unsubscribeBaskets } from 'reducks/basket/operations'
import { countOrderTimes, fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getBaskets } from 'reducks/basket/selectors'
import { getOrders, getOrderTimes } from 'reducks/checkout/selectors'
import { BlueButton, NavButton, ScrollTopButton } from 'components/Button'
import { fetchDailyStocks } from 'reducks/stock/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { FlexRow } from 'components/Flex'
import { useSelectSort, useSumOrderAmount } from 'hooks/order'
import { sortOrderOptions } from 'constants/options'
import { StockUnionType } from 'types/stock'
import { getUsers } from 'reducks/user/selectors'
import { fetchUsers } from 'reducks/user/operations'
import { fetchSchedules } from 'reducks/schedule/operations'

type Props = {
  currentDate: Date
  date: string | undefined
  isFuture: boolean
  isToday: boolean
  pathname: string
}

const OrderTemplate: FC<Props> = (props) => {
  const { currentDate, date, isFuture, isToday, pathname } = props

  const dispatch = useDispatch()
  const orderTimes: OrderTime[] = useSelector((state: State) => getOrderTimes(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeBaskets: BasketType[] = useSelector((state: State) => getBaskets(state))
  const storeOrders = useSelector((state: State) => getOrders(state))
  const users = useSelector((state: State) => getUsers(state))
  const storeRefs = useMemo(() => [storeRef], [storeRef])

  const [keyword, setKeyword] = useState<string>('')
  const [baskets, setBaskets] = useState<BasketType[]>([])
  const [orders, setOrders] = useState<OrderType[]>([])
  // 注文の表示デフォルト...当日・未来「準備が必要」, 過去「すべて」
  const [status, setStatus] = useState<string>(isFuture || isToday ? '準備が必要' : 'すべて')
  const [checkedOrderTypes, setCheckedOrderTypes] = useState<string[]>(['daily', 'preorder'])
  const [sortKey, sortOrder, sortLabel, selectSort] = useSelectSort()

  const handleKeyword = useStringChangeEvent(setKeyword)
  const handleCheckedOrderTypes = useCheckbox(setCheckedOrderTypes)
  const selectStatus = useSelectCallback(setStatus)

  // 受取番号に対応するユーザー情報を取得する
  const userIds = useMemo(() => baskets.map((basket) => basket.user_id), [baskets])
  // 現在表示中の注文件数
  const orderAmount = useSumOrderAmount(userIds, orders)

  // フォームに入力された値を部分一致で検索
  const searchKeyword = useCallback(
    (keyword: string) => {
      const baskets = storeBaskets.filter((basket) => {
        const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
        const user = users.find((user) => user.user_id === basket.user_id)
        // 受取番号か注文者名で部分一致
        return regexp.test(basket.basket_number) || regexp.test(user ? user.name : '')
      })
      setBaskets([...baskets])
    },
    [storeBaskets, setBaskets, users]
  )

  const printOrders = useCallback(() => {
    dispatch(
      push(
        `/order/print/${date}?status=${status}&type=${checkedOrderTypes.join(
          ','
        )}&sortKey=${sortKey}&sortOrder=${sortOrder}`
      )
    )
  }, [date, status, checkedOrderTypes, sortKey, sortOrder])

  const tabs = [
    { isSelected: true, label: '受取番号別', url: pathname },
    { isSelected: false, label: '商品別', url: pathname + '/product' },
  ]
  const statuses = ['準備が必要', '準備済', '受渡済', 'すべて']
  const orderTypes = ['daily', 'preorder']

  // 日付が変わったら受取番号を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initBaskets())
    dispatch(fetchBaskets(date, storeRef))
    return () => {
      dispatch(unsubscribeBaskets())
    }
  }, [date, storeRef])

  // 日付が変わったら在庫を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(fetchDailyStocks(date, storeRef))
  }, [date, storeRef])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'order'))
  }, [storeRef])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchSchedules(storeRef, ['daily', 'preorder']))
  }, [storeRef])

  // 注文回数を取得
  useEffect(() => {
    if (!currentDate || !storeRefs || !userIds || storeRefs.length === 0 || userIds.length === 0) return
    dispatch(countOrderTimes(userIds, currentDate, storeRefs))
  }, [userIds, currentDate, storeRefs])

  // 顧客情報を取得
  useEffect(() => {
    if (!userIds || userIds.length === 0) return
    dispatch(fetchUsers(userIds))
  }, [userIds])

  // ステータスまたは注文タイプが変更されたら注文情報を再取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrders(
        date,
        status,
        checkedOrderTypes as StockUnionType[],
        status === 'すべて' ? ['succeeded', 'requires_capture', 'canceled'] : ['succeeded', 'requires_capture'],
        storeRef,
        sortKey,
        sortOrder
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [checkedOrderTypes, date, sortKey, sortOrder, status, storeRef])

  // Firestoreのbasketsとordersを取得→Redux Storeの更新が完了したら、local stateに反映する
  useEffect(() => {
    setKeyword('')
    setBaskets([...storeBaskets])
    setOrders([...storeOrders])
  }, [storeBaskets, storeOrders])

  // 臨時休業日・営業日を取得（注文が1件もない場合の一斉通知のために使用する）
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // 検索フォームが空になったら検索結果をリセット(初回のレンダリングはスキップ)
  // TODO storeBasketsがdepsに必要か検証
  useSkip(
    () => {
      if (keyword !== '') return
      setBaskets([...storeBaskets])
    },
    [keyword, storeBaskets],
    1
  )

  return (
    <div className={styles.standard}>
      <Tabs tabs={tabs} />
      <Spacer size={73} />
      <FlexRow justifyContent={'end'}>
        <BlueButton
          disabled={baskets.length === 0 || orders.length === 0}
          label={'印刷する'}
          onClick={printOrders}
          size={'xsmall'}
          type={'main'}
        />
      </FlexRow>
      <Spacer size={20} />
      <SearchForm
        dataCy={'order'}
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchKeyword(keyword)}
        placeholder={'受取番号や注文者で検索'}
        value={keyword}
      />
      <Spacer size={16} />
      <FilterLabel label={'ステータス：'} />
      <Spacer size={8} />
      <OrderStatusLabels selectStatus={selectStatus} status={status} statuses={statuses} />
      <Spacer size={16} />
      <FilterLabel label={'注文タイプ：'} />
      <Spacer size={8} />
      <OrderTypeCheckboxes
        checkedOrderTypes={checkedOrderTypes}
        orderTypes={orderTypes}
        handleCheckbox={handleCheckedOrderTypes}
      />
      <Spacer size={16} />
      <FilterLabel label={'並び替え：'} />
      <Spacer size={8} />
      <SelectBox dataCy={'order'} options={sortOrderOptions} select={selectSort} size={'medium'} value={sortLabel} />
      <Spacer size={28} />
      <SmallHeadline label={`${orderAmount}件の注文を表示中`} />
      <Spacer size={12} />
      <OrderContainer
        baskets={baskets}
        isPast={!isFuture && !isToday}
        orders={orders}
        orderTimes={orderTimes}
        sortKey={sortKey}
        sortOrder={sortOrder}
        storeBaskets={storeBaskets}
        users={users}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
      <ScrollTopButton />
      <Spacer size={20} />
    </div>
  )
}

export default OrderTemplate
